import { useMemo } from "react";
import { useLanguage } from "../context/LanguageContext";
import { businessTranslations } from "../legacy/data/legacyBusinessTranslations.data";
import { businessesData } from "../legacy/data/legacyBusinesses.data";
import type { BaseBusiness, Business } from "../legacy/types/legacyBusiness.types";
import { resolveBusinessId } from "../routes/businessRoutes";

const fallbackLabelFromId = (id: string) =>
  id
    .split("-")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");

const localizeBusiness = (business: BaseBusiness, language: "es" | "en"): Business => {
  const translation = businessTranslations[language][business.id];

  return {
    ...business,
    name: translation?.name ?? fallbackLabelFromId(business.id),
    shortDescription: translation?.shortDescription ?? "",
    longDescription: translation?.longDescription ?? "",
    products: business.products.map((product) => ({
      ...product,
      name: translation?.products?.[product.id]?.name ?? fallbackLabelFromId(product.id),
      description: translation?.products?.[product.id]?.description ?? "",
    })),
    tours: business.tours?.map((tour) => ({
      ...tour,
      name: translation?.tours?.[tour.id]?.name ?? fallbackLabelFromId(tour.id),
      description: translation?.tours?.[tour.id]?.description ?? "",
      status: translation?.tours?.[tour.id]?.status,
    })),
    videos: business.videos?.map((video) => ({
      ...video,
      name: translation?.videos?.[video.id]?.name ?? fallbackLabelFromId(video.id),
      description: translation?.videos?.[video.id]?.description ?? "",
    })),
    soda: business.soda
      ? {
          ...business.soda,
          description: translation?.soda?.description ?? "",
        }
      : undefined,
    artesanias: business.artesanias
      ? {
          description: translation?.artesanias?.description ?? "",
          status: translation?.artesanias?.status,
        }
      : undefined,
    images: business.images.map((image) => ({
      ...image,
      name: image.name ?? fallbackLabelFromId(image.id),
    })),
  };
};

export const useBusinesses = () => {
  const { language } = useLanguage();

  const businesses = useMemo(
    () => businessesData.map((business) => localizeBusiness(business, language)),
    [language]
  );

  const getBusinessById = (id: string) =>
    businesses.find((business) => business.id === resolveBusinessId(id));

  return {
    businesses,
    getBusinessById,
  };
};
